import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Autocomplete,
  Typography,
  Alert,
} from '@mui/material';
import DataContext from '../../Contexts/DataContext';
import { validateMasterStatHierarchy, generateMasterStatGlKey } from '../../Helpers/masterStatCalculations';
import { getMasterStatAuditTrail, addCommentToMasterStat } from '../../Helpers/dataApi';
import useCookie from '../../Helpers/cookies';
import AuditTrailDisplay from '../../Pages/ProjectHome/Change/AuditTrailDisplay';
import CommentInput from '../../Pages/ProjectHome/Change/CommentInput';
import EditSuccessAlert from '../../Pages/ProjectHome/Change/EditSuccessAlert';

export default function MasterStatForm({ masterStat, onSave, onCancel }) {
  const context = useContext(DataContext);
  const [activeUser] = useCookie('activeUser', false);
  const { activeProjectAttributes, activeProject, activeProjectInfo } = context;
  const isMountedRef = useRef(true);

  const [formData, setFormData] = useState({
    master_stat_name: '',
    entity_id: null,
    department_id: null,
    child_stats: [],
  });

  const [errors, setErrors] = useState([]);
  const [entityOptions, setEntityOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [childStatOptions, setChildStatOptions] = useState([]);

  // Audit trail state
  const [auditTrail, setAuditTrail] = useState([]);
  const [showEditSuccess, setShowEditSuccess] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [initialComment, setInitialComment] = useState('');

  // Track component mount state to prevent updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Helper function to get ID from option (handles both id and entity_id/department_id)
  const getOptionId = useCallback((option, idField) => {
    if (!option) return null;
    // Try multiple possible property names
    return option.id || option[idField] || option[idField.replace(/([A-Z])/g, '_$1').toLowerCase()] || null;
  }, []);

  // Helper function to find option by ID
  const findOptionById = useCallback((options, id, idField) => {
    if (!id || !options) return null;
    // Try to match against multiple possible property names
    return options.find(opt => {
      const optId = opt.id || opt[idField] || opt[idField.replace(/([A-Z])/g, '_$1').toLowerCase()];
      return optId === id;
    }) || null;
  }, []);

  // Helper function to clear dependent fields when parent changes
  const clearDependentFields = useCallback((fieldsToNull) => {
    setFormData(prev => ({
      ...prev,
      ...fieldsToNull,
    }));
  }, []);

  // Fetch audit trail when editing existing Master Stat
  const fetchAuditTrail = useCallback(async (masterStatId) => {
    if (!activeProjectInfo?.id || !masterStatId) return;
    
    try {
      const accessToken = activeUser?.accessToken;
      
      if (!accessToken) {
        console.error('No access token available');
        return;
      }
      
      const response = await getMasterStatAuditTrail(
        activeProjectInfo.id,
        masterStatId,
        accessToken
      );
      
      // getMasterStatAuditTrail returns an Axios response or an error object (it does not throw).
      // Check response.status before using response.data.
      if (response && response.status === 200 && response.data) {
        if (isMountedRef.current) {
          setAuditTrail(response.data);
        }
      } else {
        console.error('Failed to fetch audit trail: unexpected response status', response?.status);
        if (isMountedRef.current) {
          setAuditTrail([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch audit trail:', error);
      if (isMountedRef.current) {
        setAuditTrail([]);
      }
    }
  }, [activeProjectInfo?.id, activeUser?.accessToken]);

  // Handle edit success
  const handleEditSuccess = useCallback(() => {
    setShowEditSuccess(true);
  }, []);

  // Handle add comment after edit
  const handleAddCommentAfterEdit = useCallback(() => {
    setShowEditSuccess(false);
    setShowCommentInput(true);
  }, []);

  // Handle comment submission
  const handleCommentSubmit = useCallback(async (commentText) => {
    if (!masterStat?.id || !activeProjectInfo?.id) return false;
    
    try {
      const accessToken = activeUser?.accessToken;
      
      if (!accessToken) {
        console.error('No access token available');
        return false;
      }
      
      const response = await addCommentToMasterStat(
        activeProjectInfo.id,
        masterStat.id,
        commentText,
        accessToken
      );
      
      // addCommentToMasterStat returns an Axios response or an error object (it does not throw).
      // Check response.status before proceeding.
      if (response && (response.status === 200 || response.status === 201)) {
        // Invalidate cache to force refetch of audit trail with new comment
        if (context?.invalidateAuditTrailCache) {
          context.invalidateAuditTrailCache(masterStat.id, 'masterStat');
        }
        
        // Refresh audit trail
        await fetchAuditTrail(masterStat.id);
        if (isMountedRef.current) {
          setShowCommentInput(false);
        }
        return true;
      } else {
        console.error('Failed to add comment: unexpected response status', response?.status);
        return false;
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
      return false;
    }
  }, [masterStat?.id, activeProjectInfo?.id, activeUser?.accessToken, fetchAuditTrail, context]);

  // Initialize form with existing Master Stat data if editing
  useEffect(() => {
    if (masterStat) {
      setFormData({
        master_stat_name: masterStat.master_stat_name || '',
        entity_id: masterStat.entity_id || null,
        department_id: masterStat.department_id || null,
        child_stats: masterStat.child_stats || [],
      });
      
      // Fetch audit trail for existing Master Stat
      if (masterStat.id) {
        fetchAuditTrail(masterStat.id);
      }
    }
  }, [masterStat, fetchAuditTrail]);

  // Use entities and departments directly from context (same as Change component)
  useEffect(() => {
    // console.log('MasterStatForm: entities from context:', activeProjectAttributes.entities);
    if (activeProjectAttributes.entities && activeProjectAttributes.entities.length > 0) {
      setEntityOptions(activeProjectAttributes.entities);
    }
  }, [activeProjectAttributes.entities]);

  // Update department options when entity changes
  useEffect(() => {
    // console.log('MasterStatForm: departments from context:', activeProjectAttributes.departments);
    // console.log('MasterStatForm: selected entity_id:', formData.entity_id);
    
    if (formData.entity_id && activeProjectAttributes.departments) {
      const filteredDepts = activeProjectAttributes.departments.filter(
        dept => {
          // console.log('Checking dept:', dept, 'against entity_id:', formData.entity_id);
          return (dept.entityId || dept.entity_id) === formData.entity_id;
        }
      );
      // console.log('Filtered departments:', filteredDepts);
      setDepartmentOptions(filteredDepts);
    } else {
      setDepartmentOptions([]);
    }
  }, [formData.entity_id, activeProjectAttributes.departments]);

  // Update child stat options when department changes
  useEffect(() => {
    // console.log('Child stats effect - entity:', formData.entity_id, 'dept:', formData.department_id);
    // console.log('accountsMetadata:', activeProjectAttributes.accountsMetadata);
    
    if (formData.entity_id && formData.department_id && activeProjectAttributes.accountsMetadata) {
      // Filter accounts to only show Stat-type accounts from the selected department
      const filteredAccounts = activeProjectAttributes.accountsMetadata.filter(
        account => {
          const matches = (account.entity_id === formData.entity_id || String(account.entity_id) === String(formData.entity_id)) &&
                 (account.department_id === formData.department_id || String(account.department_id) === String(formData.department_id)) &&
                 account.acct_type === 'Stat';
          
          if (matches) {
            // console.log('Found matching Stat account:', account.account_name);
          }
          return matches;
        }
      );

      // console.log('Filtered stat accounts:', filteredAccounts.length);

      const options = filteredAccounts.map(account => ({
        label: account.account_name,
        gl_key: `${account.entity_id}-${account.department_id}-${account.account_id}`,
        account_id: account.account_id,
      }));

      setChildStatOptions(options);
    } else {
      setChildStatOptions([]);
    }
  }, [formData.entity_id, formData.department_id, activeProjectAttributes.accountsMetadata]);

  const handleSubmit = async () => {
    // Validate
    if (!formData.master_stat_name.trim()) {
      setErrors(['Master Stat name is required']);
      return;
    }

    if (!formData.entity_id || !formData.department_id) {
      setErrors(['Entity and Department are required']);
      return;
    }

    // Validate hierarchy - pass treeData if activeProject exists
    if (activeProject && activeProject.treeData) {
      const validation = validateMasterStatHierarchy(formData.child_stats, activeProject.treeData);
      
      if (!validation.valid) {
        setErrors(validation.errors);
        return;
      }
    }

    // Backend will generate the GL key, so we don't send it
    const masterStatData = {
      master_stat_name: formData.master_stat_name,
      entity_id: formData.entity_id,
      department_id: formData.department_id,
      child_stats: formData.child_stats,
    };

    // Add initial comment for new Master Stats
    if (!masterStat && initialComment.trim()) {
      masterStatData.user_note = initialComment.trim();
    }

    // Call onSave callback and check if it succeeded
    const success = await onSave(masterStatData);

    // If editing and save succeeded and still mounted, fetch updated audit trail and show success alert
    // Note: The parent component may close/unmount this form after onSave succeeds,
    // so we check if we're still mounted before updating state
    if (success && masterStat?.id && isMountedRef.current) {
      await fetchAuditTrail(masterStat.id);
      handleEditSuccess();
    }
  };

  return (
    <Box className="master-stat-form">
      <Typography variant="h6" sx={{ mb: 3 }}>
        {masterStat ? 'Edit Master Stat' : 'Add New Master Stat'}
      </Typography>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.map((error, idx) => (
            <div key={idx}>{error}</div>
          ))}
        </Alert>
      )}

      <Box className="form-section">
        <TextField
          fullWidth
          label="Master Stat Name"
          value={formData.master_stat_name}
          onChange={(e) => setFormData({ ...formData, master_stat_name: e.target.value })}
          required
          sx={{ mb: 2 }}
        />
      </Box>

      <Box className="form-section">
        <Autocomplete
          fullWidth
          options={entityOptions}
          getOptionLabel={(option) => option.name || option.entity_name || option.entityName || ''}
          value={findOptionById(entityOptions, formData.entity_id, 'entity_id')}
          onChange={(e, newValue) => {
            const newEntityId = newValue ? (newValue.entity_id || newValue.entityId || newValue.id) : null;
            setFormData({
              ...formData,
              entity_id: newEntityId,
              department_id: null,
              child_stats: [],
            });
          }}
          renderInput={(params) => (
            <TextField {...params} label="Entity" required />
          )}
          sx={{ mb: 2 }}
        />
      </Box>

      <Box className="form-section">
        <Autocomplete
          fullWidth
          options={departmentOptions}
          getOptionLabel={(option) => option.name || option.department_name || option.departmentName || ''}
          value={findOptionById(departmentOptions, formData.department_id, 'department_id')}
          onChange={(e, newValue) => {
            const newDeptId = newValue ? (newValue.department_id || newValue.departmentId || newValue.id) : null;
            setFormData({
              ...formData,
              department_id: newDeptId,
              child_stats: [],
            });
          }}
          renderInput={(params) => (
            <TextField {...params} label="Department" required />
          )}
          sx={{ mb: 2 }}
          disabled={!formData.entity_id}
        />
      </Box>

      <Box className="child-stats-section">
        <Typography className="section-label" variant="subtitle1">
          Child Stats
        </Typography>
        <Autocomplete
          multiple
          fullWidth
          disableCloseOnSelect
          options={childStatOptions}
          getOptionLabel={(option) => option.label || ''}
          value={childStatOptions.filter(opt => formData.child_stats.includes(opt.gl_key))}
          onChange={(e, newValue) => {
            setFormData({
              ...formData,
              child_stats: newValue.map(v => v.gl_key),
            });
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select Child Stats" placeholder="Search stats..." />
          )}
          disabled={!formData.department_id}
        />
      </Box>

      {/* Initial Comment for New Master Stats */}
      {!masterStat && (
        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Add Initial Comment (Optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={initialComment}
            onChange={(e) => {
              if (e.target.value.length <= 1000) {
                setInitialComment(e.target.value);
              }
            }}
            placeholder="Add a note about this Master Stat..."
            helperText={`${initialComment.length}/1000 characters`}
          />
        </Box>
      )}

      {/* Comments & History for Editing */}
      {masterStat && (
        <Box sx={{ mt: 3, mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Comments & History
          </Typography>

          {/* Edit Success Alert */}
          {showEditSuccess && masterStat && (
            <EditSuccessAlert
              onAddComment={handleAddCommentAfterEdit}
              onReturnHome={onCancel}
              onDismiss={() => setShowEditSuccess(false)}
            />
          )}

          {/* Comment Input */}
          {showCommentInput && masterStat && (
            <CommentInput
              onSubmit={handleCommentSubmit}
              onCancel={() => setShowCommentInput(false)}
            />
          )}

          {/* Audit Trail Display */}
          {masterStat && !showCommentInput && (
            <AuditTrailDisplay auditTrail={auditTrail} />
          )}

          {/* Add Comment Button */}
          {masterStat && !showEditSuccess && !showCommentInput && (
            <Button
              variant="outlined"
              onClick={() => setShowCommentInput(true)}
              sx={{ mt: 2 }}
            >
              Add Comment
            </Button>
          )}
        </Box>
      )}

      <Box className="form-actions">
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Save
        </Button>
      </Box>
    </Box>
  );
}
